import React, { useState } from "react";
import axios from "axios";
import { X, Download, Loader2 } from "lucide-react";
import { toast } from "react-toastify";

const LeadGenerationModal = ({ isOpen, onClose, selectedPlan }) => {
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    location: "",
    education: "",
    experience: "",
  });

  if (!isOpen) {
    return null;
  }

  // ========================================
  // INPUT CHANGE
  // ========================================

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ========================================
  // SUBMIT
  // ========================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    // -----------------------------
    // VALIDATION
    // -----------------------------

    if (!formData.name.trim()) {
      toast.error("Please enter your name");
      return;
    }

    if (!formData.email.trim()) {
      toast.error("Please enter your email");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error("Please enter a valid email");
      return;
    }

    if (!formData.phone.trim()) {
      toast.error("Please enter your phone number");
      return;
    }

    // Make sure brochure exists
    const brochureUrl = selectedPlan?.brochure;

    if (!brochureUrl) {
      toast.error("Brochure is not available.");
      return;
    }

    // -----------------------------------------
    // IMPORTANT:
    // Open a blank tab BEFORE axios request.
    // This prevents popup blocker issues.
    // -----------------------------------------

    const brochureWindow = window.open("", "_blank");

    if (brochureWindow) {
      brochureWindow.document.write(`
      <html>
        <head>
          <title>Preparing your brochure...</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              display: flex;
              align-items: center;
              justify-content: center;
              height: 100vh;
              margin: 0;
              background: #f8fafc;
            }

            .box {
              text-align: center;
            }

            .loader {
              width: 40px;
              height: 40px;
              border: 4px solid #e5e7eb;
              border-top: 4px solid #2563eb;
              border-radius: 50%;
              animation: spin 1s linear infinite;
              margin: 0 auto 20px;
            }

            @keyframes spin {
              100% {
                transform: rotate(360deg);
              }
            }
          </style>
        </head>

        <body>
          <div class="box">
            <div class="loader"></div>
            <h3>Preparing your brochure...</h3>
            <p>Please wait...</p>
          </div>
        </body>
      </html>
    `);
      brochureWindow.document.close();
    }

    try {
      setLoading(true);

      // -----------------------------------------
      // SAVE LEAD
      // -----------------------------------------

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/leads/createleads`,
        {
          ...formData,

          planName:
            selectedPlan?.name || selectedPlan?.planName || "Unknown Plan",
        },
      );

      if (!response.data?.success) {
        throw new Error(
          response.data?.message ||
            response.data?.error ||
            "Failed to submit lead",
        );
      }

      // -----------------------------------------
      // LEAD SUCCESS
      // -----------------------------------------

      toast.success("Thank you! Your brochure is opening...");

      // -----------------------------------------
      // OPEN BROCHURE
      // -----------------------------------------

      if (brochureWindow && !brochureWindow.closed) {
        brochureWindow.location.href = brochureUrl;
      } else {
        // Fallback if browser blocked the tab
        window.location.href = brochureUrl;
      }

      onClose();

      // -----------------------------------------
      // RESET FORM
      // -----------------------------------------

      setFormData({
        name: "",
        email: "",
        phone: "",
        location: "",
        education: "",
        experience: "",
      });
    } catch (error) {
      console.error("Lead submission error:", error);

      // Close blank brochure tab if lead failed
      if (brochureWindow && !brochureWindow.closed) {
        brochureWindow.close();
      }

      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Something went wrong. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white rounded-3xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ========================================
            HEADER
        ======================================== */}

        <div className="sticky top-0 z-10 bg-white border-b border-gray-100 px-6 md:px-8 py-5 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Download Brochure
            </h2>

            <p className="text-sm text-gray-500 mt-1">
              Please provide your details to continue
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition"
          >
            <X size={20} />
          </button>
        </div>

        {/* ========================================
            SELECTED PLAN
        ======================================== */}

        <div className="px-6 md:px-8 pt-6">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-2xl p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold">
              Selected Plan
            </p>

            <h3 className="text-lg font-bold text-gray-900 mt-1">
              {selectedPlan?.name || selectedPlan?.planName}
            </h3>
          </div>
        </div>

        {/* ========================================
            FORM
        ======================================== */}

        <form onSubmit={handleSubmit} className="p-6 md:p-8 space-y-5">
          {/* Name */}

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Full Name *
            </label>

            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter your full name"
              className="w-full px-4 py-3 border border-gray-200 rounded-xl outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition"
            />
          </div>

          {/* Email */}

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Email Address *
            </label>

            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email"
              className="w-full px-4 py-3 border border-gray-200 rounded-xl outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition"
            />
          </div>

          {/* Phone */}

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Phone Number *
            </label>

            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="Enter your phone number"
              maxLength={10}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition"
            />
          </div>

          {/* Location */}

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Location
            </label>

            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleChange}
              placeholder="City / Location"
              className="w-full px-4 py-3 border border-gray-200 rounded-xl outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition"
            />
          </div>

          {/* Education */}

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Education
            </label>

            <input
              type="text"
              name="education"
              value={formData.education}
              onChange={handleChange}
              placeholder="e.g. B.Tech, MBA, BCA"
              className="w-full px-4 py-3 border border-gray-200 rounded-xl outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition"
            />
          </div>

          {/* Experience */}

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Experience
            </label>

            <input
              type="text"
              name="experience"
              value={formData.experience}
              onChange={handleChange}
              placeholder="e.g. Fresher, 2 Years"
              className="w-full px-4 py-3 border border-gray-200 rounded-xl outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition"
            />
          </div>

          {/* ========================================
              SUBMIT
          ======================================== */}

          <button
            type="submit"
            disabled={loading}
            className="w-full mt-3 flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed text-white py-4 rounded-xl font-semibold text-base transition-all duration-300 shadow-lg"
          >
            {loading ? (
              <>
                <Loader2 size={20} className="animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Download size={20} />
                Submit & Download Brochure
              </>
            )}
          </button>

          <p className="text-center text-xs text-gray-400">
            Your information is safe and will only be used to contact you
            regarding Techmentee programs.
          </p>
        </form>
      </div>
    </div>
  );
};

export default LeadGenerationModal;

// import React, { useState, useEffect } from 'react';
// import { X, Download, User, Mail, Phone, MapPin, GraduationCap } from 'lucide-react';
// import axios from 'axios';
// import { toast } from 'react-toastify';

// const LeadGenerationModal = ({ isOpen, onClose, selectedPlan  , type}) => {
//     const [formData, setFormData] = useState({
//         name: '',
//         email: '',
//         phone: '',
//         location: '',
//         education: '',
//         experience: '',
//         planName: selectedPlan?.planName || ''
//     });

//     const [isSubmitting, setIsSubmitting] = useState(false);
//     const [errors, setErrors] = useState({});

//     // Update planName when selectedPlan changes
//     useEffect(() => {
//         console.log("LeadGenerationModal selectedPlan:", selectedPlan);
//         if (selectedPlan?.planName) {
//             setFormData(prev => ({
//                 ...prev,
//                 planName: type? selectedPlan.planName : selectedPlan.name,
//             }));
//         }
//     }, [selectedPlan]);

//     const validateForm = () => {
//         const newErrors = {};

//         if (!formData.name.trim()) newErrors.name = 'Name is required';
//         if (!formData.email.trim()) newErrors.email = 'Email is required';
//         else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
//         if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
//         else if (!/^\+?[\d\s\-\(\)]+$/.test(formData.phone)) newErrors.phone = 'Phone number is invalid';
//         if (!formData.location.trim()) newErrors.location = 'Location is required';
//         if (!formData.education.trim()) newErrors.education = 'Education is required';

//         setErrors(newErrors);
//         return Object.keys(newErrors).length === 0;
//     };

//     const handleInputChange = (e) => {
//         const { name, value } = e.target;
//         setFormData(prev => ({
//             ...prev,
//             [name]: value
//         }));

//         // Clear error when user starts typing
//         if (errors[name]) {
//             setErrors(prev => ({
//                 ...prev,
//                 [name]: ''
//             }));
//         }
//     };

//     const handleClear = () => {
//         setFormData({
//             name: '',
//             email: '',
//             phone: '',
//             location: '',
//             education: '',
//             experience: '',
//             planName: selectedPlan?.planName || ''
//         });
//     };

// //     useEffect(() => {
// //     console.log("LeadGenerationModal selectedPlan:", selectedPlan);
// // }, [selectedPlan]);

//     // Function to handle PDF download
// const downloadBrochure = () => {
//     if (!selectedPlan?.brochure) {
//         toast.error("Brochure not available.");
//         return;
//     }
//     const link = document.createElement("a");
//     link.href = selectedPlan.brochure;
//     link.target = "_blank";
//     link.download = "";
//     document.body.appendChild(link);
//     link.click();
//     document.body.removeChild(link);
// };

//     const showToast = (message, type = 'success') => {
//         toast[`${type}`](`${message}`);
//         // alert(`${type.toUpperCase()}: ${message}`);
//     };

//     const handleSubmit = async () => {
//         if (!validateForm()) return;

//         setIsSubmitting(true);

//         try {
//             const config = {
//                 method: "post",
//                 maxBodyLength: Infinity,
//                 url: `${process.env.REACT_APP_BASE_URL}/leads/createleads`,
//                 headers: {
//                     "Content-Type": "application/json",
//                 },
//                 data: formData,
//             };

//             const response = await axios.request(config);

//             console.log("res", response.data);
//             handleClear();
//             showToast("Successfully submitted your request");
//             // console.log("selectplan", selectedPlan)
//             downloadBrochure();
//             toast.success('Thank you for your interest! The brochure download has been initiated.');
//             onClose();

//         } catch (error) {
//             console.log(error);
//             showToast("Error while submitting the request", "error");
//         } finally {
//             setIsSubmitting(false);
//         }
//     };

//     if (!isOpen) return null;

//     return (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//             <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
//                 {/* Header */}
//                 <div className="bg-gradient-to-r from-orange-400 to-orange-500 text-white p-6 rounded-t-2xl relative">
//                     <button
//                         onClick={onClose}
//                         className="absolute top-4 right-4 text-white hover:text-gray-200 transition-colors"
//                     >
//                         <X size={24} />
//                     </button>
//                     <div className="flex items-center gap-3">
//                         <Download className="w-8 h-8" />
//                         <div>
//                             <h2 className="text-2xl font-bold">Get Your Free Brochure</h2>
//                             <p className="text-orange-100">
//                                 Download detailed information about the {selectedPlan?.planName} plan
//                             </p>
//                         </div>
//                     </div>
//                 </div>

//                 {/* Plan Summary */}
//                 <div className="bg-gradient-to-r from-blue-50 to-orange-50 p-6 border-b">
//                     <div className="text-center">
//                         <h3 className="text-xl font-bold text-gray-800 mb-2">
//                             {selectedPlan?.planName} Plan
//                         </h3>
//                         <div className="flex items-baseline justify-center gap-1 mb-2">
//                             <span className="text-2xl font-medium text-gray-600">$</span>
//                             <span className="text-4xl font-black text-gray-900">{selectedPlan?.price}</span>
//                         </div>
//                         <p className="text-gray-600 text-sm max-w-md mx-auto">
//                             {selectedPlan?.description}
//                         </p>
//                     </div>
//                 </div>

//                 {/* Form */}
//                 <div className="p-6">
//                     <div className="mb-6">
//                         <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
//                             <User className="w-5 h-5 text-orange-500" />
//                             Your Information
//                         </h3>
//                     </div>

//                     <div className="mb-4">
//                         <label className=" text-sm font-medium text-gray-700 mb-2">
//                             Name *
//                         </label>
//                         <input
//                             type="text"
//                             name="name"
//                             value={formData.name}
//                             onChange={handleInputChange}
//                             className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all ${errors.name ? 'border-red-500' : 'border-gray-300'
//                                 }`}
//                             placeholder="Enter your full name"
//                         />
//                         {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
//                     </div>

//                     <div className="mb-4">
//                         <label className=" text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
//                             <Mail className="w-4 h-4 text-orange-500" />
//                             Email Address *
//                         </label>
//                         <input
//                             type="email"
//                             name="email"
//                             value={formData.email}
//                             onChange={handleInputChange}
//                             className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all ${errors.email ? 'border-red-500' : 'border-gray-300'
//                                 }`}
//                             placeholder="Enter your email address"
//                         />
//                         {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
//                     </div>

//                     <div className="mb-4">
//                         <label className=" text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
//                             <Phone className="w-4 h-4 text-orange-500" />
//                             Phone Number *
//                         </label>
//                         <input
//                             type="tel"
//                             name="phone"
//                             value={formData.phone}
//                             onChange={handleInputChange}
//                             className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all ${errors.phone ? 'border-red-500' : 'border-gray-300'
//                                 }`}
//                             placeholder="+1 (555) 123-4567"
//                         />
//                         {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
//                     </div>

//                     <div className="mb-4">
//                         <label className=" text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
//                             <MapPin className="w-4 h-4 text-orange-500" />
//                             Current Location *
//                         </label>
//                         <input
//                             type="text"
//                             name="location"
//                             value={formData.location}
//                             onChange={handleInputChange}
//                             className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all ${errors.location ? 'border-red-500' : 'border-gray-300'
//                                 }`}
//                             placeholder="City, State/Country"
//                         />
//                         {errors.location && <p className="text-red-500 text-sm mt-1">{errors.location}</p>}
//                     </div>

//                     <div className="mb-4">
//                         <label className=" text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
//                             <GraduationCap className="w-4 h-4 text-orange-500" />
//                             Education Level *
//                         </label>
//                         <select
//                             name="education"
//                             value={formData.education}
//                             onChange={handleInputChange}
//                             className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all ${errors.education ? 'border-red-500' : 'border-gray-300'
//                                 }`}
//                         >
//                             <option value="">Select your education level</option>
//                             <option value="high-school">High School</option>
//                             <option value="some-college">Some College</option>
//                             <option value="bachelors">Bachelor's Degree</option>
//                             <option value="masters">Master's Degree</option>
//                             <option value="phd">PhD</option>
//                             <option value="other">Other</option>
//                         </select>
//                         {errors.education && <p className="text-red-500 text-sm mt-1">{errors.education}</p>}
//                     </div>

//                     <div className="mb-6">
//                         <label className=" text-sm font-medium text-gray-700 mb-2">
//                             Years of Experience (Optional)
//                         </label>
//                         <select
//                             name="experience"
//                             value={formData.experience}
//                             onChange={handleInputChange}
//                             className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
//                         >
//                             <option value="">Select your experience level</option>
//                             <option value="0">No experience</option>
//                             <option value="1-2">1-2 years</option>
//                             <option value="3-5">3-5 years</option>
//                             <option value="6-10">6-10 years</option>
//                             <option value="10+">10+ years</option>
//                         </select>
//                     </div>

//                     {/* Submit Button */}
//                     <div className="flex gap-4">
//                         <button
//                             type="button"
//                             onClick={onClose}
//                             className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
//                         >
//                             Cancel
//                         </button>
//                         <button
//                             type="button"
//                             onClick={handleSubmit}
//                             disabled={isSubmitting}
//                             className="flex-1 bg-gradient-to-r from-orange-400 to-orange-500 text-white px-6 py-3 rounded-lg hover:from-orange-500 hover:to-orange-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
//                         >
//                             {isSubmitting ? (
//                                 <>
//                                     <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
//                                     Submitting...
//                                 </>
//                             ) : (
//                                 <>
//                                     <Download className="w-4 h-4" />
//                                     Download Brochure
//                                 </>
//                             )}
//                         </button>
//                     </div>
//                 </div>

//                 {/* Footer */}
//                 <div className="bg-gray-50 px-6 py-4 rounded-b-2xl">
//                     <p className="text-xs text-gray-600 text-center">
//                         By submitting this form, you agree to receive communications from TechMentee.
//                         Your information will be kept secure and never shared with third parties.
//                     </p>
//                 </div>
//             </div>
//         </div>
//     );
// };

// export default LeadGenerationModal;
